import { useRef, useState, useEffect } from 'react'
import WireframeArt from '../../../../assets/images/admin/wireframe-art.png'
import ButtonIcon from '../../../../assets/images/icons/button-icon.svg';
import MultitrackIcon from '../../../../assets/images/icons/multitrack.svg';
import MixesIcon from '../../../../assets/images/icons/mixes.svg';
import MastersIcon from '../../../../assets/images/icons/masters.svg';
import PieGroupIcon from '../../../../assets/images/icons/pie-group.svg';
import GoalIcon from '../../../../assets/images/icons/goal.svg';
import WaveIcon from '../../../../assets/images/icons/wave.svg';
import BrushIcon from '../../../../assets/images/icons/cleaner-icon.svg';
import { Tooltip } from '../../../../components/Tooltip';
import MixesLibrary from './MixesLibrary/MixesLibrary';
import MastersLibrary from './MastersLibrary/MastersLibrary';
import { CategoryComboBox, categories as defaultCategories } from './components/CategoryComboBox';
import { IconDropdown } from './components/IconDropdown';
import { OptionsMenu } from './components/OptionsMenu';
import { EditableTrackName } from './components/EditableTrackName';
import PreviewSection from './PreviewSection';
import { GenreDropdown } from './components/GenreDropdown';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { createMixPreview, getAudioClean, uploadAndGetPreview, mixPreviewData, uploadProjectImage, renameMixTrack, uploadData, clearUploadData } from '../../../../redux/actions/audioEngineer';
import { showToast } from '../../../../helper/ShowToast';
import { inferCategoryFromName } from '../../../../helper/extractCategory';
import { toast } from 'react-toastify';
import ProcessingLoader from './components/ProcessingLoader';
import { useNavigate } from 'react-router-dom';
import { consumeCredits } from '../../../../redux/actions';
import { PricingPlanModal } from '../../../../components/PricingPlanModal';
import { CreditPlanModal } from '../../../../components/CreditPlanModal';
import { AudioCleanPackModal } from './AudioCleanPackModal';

const importanceLevels = ["Normal", "Lead", "Background"]
const reverbLevels = ["None", "Low", "Medium", "High"]

const demoTracksData = [
    {
        id: 'demo-track-1',
        name: 'Lead Vocal.wav',
        selected: true,
        playing: false,
        playingCleaned: false,
        cleanerEnabled: true,
        cleaned: false,
        cleanedPreview: true,
        cleanedAudioUrl: '#',
        pan: 'C',
        category: 'VOCAL_GROUP',
        duration: '3:24',
        roexStatus: 'UPLOADED_TO_ROEX',
        importance: 'Lead',
        reverb: 'Medium',
    },
    {
        id: 'demo-track-2',
        name: 'Acoustic Guitar.wav',
        selected: true,
        playing: false,
        cleanerEnabled: false,
        pan: 'L',
        category: 'ACOUSTIC_GUITAR_GROUP',
        duration: '2:51',
        roexStatus: 'UPLOADED_TO_ROEX',
        importance: 'Normal',
        reverb: 'Low',
    }
]

const tabs = [
    { id: "multitrack", label: "Multitrack", icon: MultitrackIcon },
    { id: "mixes", label: "Mixes", icon: MixesIcon },
    { id: "masters", label: "Masters", icon: MastersIcon },
]

const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
};

const Multitrack = ({ demoMode = false }) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState("multitrack")
    const [tracks, setTracks] = useState([])
    const [cleanAudioLoading, setCleanAudioLoading] = useState(false)
    const [showLoader, setShowLoader] = useState(false)
    const localGenre = localStorage.getItem('genre');
    const [selectedGenre, setSelectedGenre] = useState(localGenre || null)
    const [showPricingPlanModal, setShowPricingPlanModal] = useState(false);
    const [showAudioCleanModal, setShowAudioCleanModal] = useState(false);
    const [showCreditPlanModal, setShowCreditPlanModal] = useState(false);
    const [reqData, setReqData] = useState(null)

    const [cleanedTrackId, setCleanedTrackId] = useState(null)
    const { uploadedData, cleanAudio, mixPreview, mixMetaData, saveCleanedAudio, uploadStatuses } = useSelector((state) => state.audioEngineer);
    const { id, projectName, projectImage } = mixMetaData || {};
    const [coverImage, setCoverImage] = useState(projectImage || WireframeArt);
    const audioRefs = useRef({});
    const fileInputRef = useRef(null)
    const coverImageInputRef = useRef(null);
    const [isUploadingLocal, setIsUploadingLocal] = useState(false)
    const [savingCleanedTrackId, setSavingCleanedTrackId] = useState(null)
    const { hasActiveSubscription, creditDetails } = useSelector((state) => state.billing);

    const fmt = (s) => {
        if (!s || isNaN(s) || !isFinite(s)) return "0:00";
        const m = Math.floor(s / 60);
        const sec = Math.floor(s % 60).toString().padStart(2, '0');
        return `${m}:${sec}`;
    }

    useEffect(() => {
        if (!demoMode) return;

        setTracks(demoTracksData);
    }, [demoMode]);

    useEffect(() => {
        if (demoMode) return;

        const uploaded = uploadedData?.data;
        if (!Array.isArray(uploaded) || uploaded.length === 0) return;

        setTracks(prev => {
            const existingMap = new Map(prev.map(t => [String(t.id), t]));
            const results = [];

            uploaded.forEach((item, idx) => {
                const id = item._id || `uploaded-${idx}`;
                const existing = existingMap.get(String(id));
                const trackName = item.filename || item.name || `Track ${idx + 1}`;
                const inferredCategory = inferCategoryFromName(trackName);

                results.push({
                    id,
                    name: trackName,
                    fileUrl: item.readable_url || item.fileUrl || null,
                    originalUrl: item.originalUrl || item.original_url || item.readable_url || null,
                    selected: existing?.selected !== undefined ? existing.selected : true,
                    playing: false,
                    cleanerEnabled:
                        existing?.cleanerEnabled !== undefined
                            ? existing.cleanerEnabled
                            : inferredCategory === 'VOCAL_GROUP',
                    pan: existing?.pan || '',
                    category: existing?.category || inferredCategory,
                    duration: existing?.duration || formatDuration(item.duration),
                    roexStatus: item.roexStatus || existing?.roexStatus || 'LOCAL_UPLOADING',
                });
            });

            const realBaseNames = new Set(
                results
                    .filter(r => !String(r.id).startsWith('uploaded-'))
                    .map(r => (r.name || '').split('/').pop().toLowerCase())
            );

            return results.filter(r => {
                if (!String(r.id).startsWith('uploaded-')) return true;
                const baseName = (r.name || '').split('/').pop().toLowerCase();
                return !realBaseNames.has(baseName);
            });

            // return results;
        });

        uploaded.forEach((item, idx) => {
            const id = item._id || `uploaded-${idx}`;
            const originalUrl = item.originalUrl || item.original_url || item.readable_url || null;
            if (originalUrl && !audioRefs.current[id]) {
                try {
                    const audio = new Audio(originalUrl);
                    audio.preload = 'metadata';
                    audio.oncanplay = () => {
                        console.log('Can play:', originalUrl);
                    };

                    audio.onerror = () => {
                        console.log('Cannot play:', originalUrl);
                        console.log('Audio Error:', audio.error);
                    };
                    const onLoaded = () => {
                        const d = audio.duration || 0;
                        setTracks(prev => prev.map(p => p.id === id ? { ...p, duration: fmt(d) } : p));
                    };
                    const onEnded = () => {
                        setTracks(prev => prev.map(p => p.id === id ? { ...p, playing: false } : p));
                    };
                    audio.addEventListener('loadedmetadata', onLoaded);
                    audio.addEventListener('ended', onEnded);
                    audioRefs.current[id] = audio;
                } catch (e) {
                    // ignore audio creation errors
                }
            }
        });
    }, [uploadedData]);

    // Sync roexStatus updates from polling into local tracks state
    useEffect(() => {
        if (!Array.isArray(uploadStatuses) || uploadStatuses.length === 0) return;

        setTracks(prev => prev.map(track => {
            const update = uploadStatuses.find(s => String(s._id) === String(track.id));
            if (update?.roexStatus && update.roexStatus !== track.roexStatus) {
                return { ...track, roexStatus: update.roexStatus };
            }
            return track;
        }));
    }, [uploadStatuses]);

    // When user switches to the Mixes tab, refresh the mixes-library query so the table shows latest data
    useEffect(() => {
        if (activeTab === 'mixes') {
            try {
                queryClient.invalidateQueries(['mixes-library']);
            } catch (e) {
                console.debug('Failed to invalidate mixes-library query', e);
            }
        }
    }, [activeTab, queryClient]);

    // cleanup audio refs on component unmount
    useEffect(() => {
        return () => {
            Object.keys(audioRefs.current).forEach((k) => {
                try { audioRefs.current[k].pause(); } catch (e) { }
                delete audioRefs.current[k];
            });
        };
    }, []);

    // When a cleanAudio redux payload arrives, apply it to the currently cleaned track (cleanedTrackId)
    useEffect(() => {
        if (!cleanAudio || !cleanedTrackId) return;

        try {
            const resp = cleanAudio;
            // locate best audio URL (prefer mp3 fallback to wav)
            const audioUrl = resp?.roex_response?.audioCleanupResults?.cleaned_audio_file_location_mp3 || resp?.roex_response?.audioCleanupResults?.cleaned_audio_file_location || resp?.data?.roex_response?.audioCleanupResults?.cleaned_audio_file_location_mp3 || resp?.data?.roex_response?.audioCleanupResults?.cleaned_audio_file_location || null;

            if (!audioUrl) return;

            // attach cleaned preview data to the track
            setTracks(prev => prev.map(t => t.id === cleanedTrackId ? {
                ...t,
                cleanedPreview: true,
                cleanedData: resp,
                cleanedAudioUrl: audioUrl,
                cleanedAudioId: resp._id || resp.data?._id || null,
            } : t));

            // create a cleaned audio ref for playback and duration extraction
            const cleanedKey = `cleaned-${cleanedTrackId}`;
            try {
                const a = new Audio(audioUrl);
                a.preload = 'metadata';
                const onLoaded = () => {
                    const d = a.duration || 0;
                    setTracks(prev => prev.map(p => p.id === cleanedTrackId ? { ...p, cleanedDuration: fmt(d) } : p));
                };
                const onEnded = () => {
                    setTracks(prev => prev.map(p => p.id === cleanedTrackId ? { ...p, playingCleaned: false } : p));
                };
                a.addEventListener('loadedmetadata', onLoaded);
                a.addEventListener('ended', onEnded);
                // store under special key
                audioRefs.current[cleanedKey] = a;
            } catch (e) {
                console.debug('Failed to create cleaned audio element', e);
            }
        } catch (err) {
            console.debug('Error applying cleanAudio to track', err);
        }
        // do not clear cleanedTrackId here; leave it so user can Keep/Discard
    }, [cleanAudio, cleanedTrackId]);

    const selectedCount = tracks.filter(t => t.selected).length

    /* GROUP TRACKS BY CATEGORY */
    const groupedTracks = tracks.reduce((acc, track) => {
        if (!acc[track.category]) acc[track.category] = []
        acc[track.category].push(track)
        return acc
    }, {})

    const toggleTrack = (id) => {
        setTracks(prev =>
            prev.map(t => (t.id === id ? { ...t, selected: !t.selected } : t))
        )
    }

    const toggleGroup = (category) => {
        setTracks(prev => {
            const group = prev.filter(t => t.category === category)
            const allSelected = group.every(t => t.selected)

            return prev.map(t =>
                t.category === category ? { ...t, selected: !allSelected } : t
            )
        })
    }

    const togglePlay = (id) => {
        const audio = audioRefs.current[id];
        if (!audio) {
            // fallback: toggle state only
            setTracks(prev => prev.map(t => t.id === id ? { ...t, playing: !t.playing } : { ...t, playing: false }));
            return;
        }

        // Pause all other audios
        Object.entries(audioRefs.current).forEach(([key, a]) => {
            if (key !== String(id) && !a.paused) {
                try { a.pause(); } catch (e) { }
            }
        });

        if (audio.paused) {
            audio.play().then(() => {
                console.log('Playing:', audio.src);
                setTracks(prev => prev.map(t => ({ ...t, playing: t.id === id })));
            }).catch(err => {
                // play failed (autoplay policy) — set playing flag false
                console.error('Play failed:', err);
                console.error('Audio Error:', audio.error);
                setTracks(prev => prev.map(t => ({ ...t, playing: false })));
                console.debug('Play failed', err);
            });
        } else {
            try { audio.pause(); } catch (e) { }
            setTracks(prev => prev.map(t => t.id === id ? { ...t, playing: false } : t));
        }
    }

    const togglePlayCleaned = (id) => {
        const key = `cleaned-${id}`;
        const audio = audioRefs.current[key];
        if (!audio) {
            setTracks(prev => prev.map(t => t.id === id ? { ...t, playingCleaned: !t.playingCleaned } : { ...t, playingCleaned: false }));
            return;
        }

        // Pause all other cleaned audios
        Object.entries(audioRefs.current).forEach(([k, a]) => {
            if (k !== key && k.startsWith('cleaned-') && !a.paused) {
                try { a.pause(); } catch (e) { }
            }
        });

        if (audio.paused) {
            audio.play().then(() => {
                setTracks(prev => prev.map(t => ({ ...t, playingCleaned: t.id === id ? true : false })));
            }).catch(err => {
                setTracks(prev => prev.map(t => ({ ...t, playingCleaned: false })));
                console.debug('Play cleaned failed', err);
            });
        } else {
            try { audio.pause(); } catch (e) { }
            setTracks(prev => prev.map(t => t.id === id ? { ...t, playingCleaned: false } : t));
        }
    }

    const setTrackPan = (id, pan) => {
        setTracks(prev => prev.map(t => t.id === id ? { ...t, pan } : t))
    }

    const setTrackCategory = (id, category) => {
        const allowed = new Set([
            'KICK_GROUP',
            'SNARE_GROUP',
            'VOCAL_GROUP',
            'BACKING_VOCALS_GROUP',
            'PERCS_GROUP',
            'STRINGS_GROUP',
            'E_GUITAR_GROUP',
            'ACOUSTIC_GUITAR_GROUP'
        ]);

        // Update only the target track's category and cleanerEnabled status
        setTracks(prev => prev.map(t => {
            if (t.id === id) {
                return { ...t, category, cleanerEnabled: allowed.has(category) };
            }
            return t; // Keep other tracks as-is, don't modify cleanerEnabled
        }));
    }

    const setTrackImportance = (id, importance) => {
        setTracks(prev => prev.map(t => t.id === id ? { ...t, importance } : t))
    }

    const setTrackName = (id, name) => {
        setTracks(prev => prev.map(t => t.id === id ? { ...t, name } : t))
    }

    const setTrackReverb = (id, reverb) => {
        setTracks(prev => prev.map(t => t.id === id ? { ...t, reverb } : t))
    }

    const toggleSelectAll = () => {
        const allSelected = tracks.every(t => t.selected)
        setTracks(prev => prev.map(t => ({ ...t, selected: !allSelected })))
    }

    const deleteSelected = () => {
        if (demoMode) return;
        // collect ids to remove
        const selectedIds = tracks.filter(t => t.selected).map(t => String(t.id));
        if (selectedIds.length === 0) return;

        // cleanup audio refs for deleted tracks
        selectedIds.forEach(id => {
            if (audioRefs.current[id]) {
                try { audioRefs.current[id].pause(); } catch (e) { }
                delete audioRefs.current[id];
            }
            const cleanedKey = `cleaned-${id}`;
            if (audioRefs.current[cleanedKey]) {
                try { audioRefs.current[cleanedKey].pause(); } catch (e) { }
                delete audioRefs.current[cleanedKey];
            }
        });

        // remove from local tracks state
        setTracks(prev => prev.filter(t => !t.selected));

        // remove matching items from uploadedData reducer (if present)
        try {
            if (uploadedData) {
                if (Array.isArray(uploadedData.data)) {
                    const filtered = uploadedData.data.filter(item => !selectedIds.includes(String(item._id)));
                    dispatch(uploadData(Object.assign({}, uploadedData, { data: filtered })));
                } else if (Array.isArray(uploadedData)) {
                    const filtered = uploadedData.filter(item => !selectedIds.includes(String(item._id)));
                    dispatch(uploadData(filtered));
                }
            }
        } catch (e) {
            console.debug('Failed to update uploadedData after deleteSelected', e);
        }
    }

    const deleteTrack = (id) => {
        if (demoMode) return;

        const sid = String(id);
        // cleanup audio ref
        if (audioRefs.current[sid]) {
            try { audioRefs.current[sid].pause(); } catch (e) { }
            delete audioRefs.current[sid];
        }
        const cleanedKey = `cleaned-${sid}`;
        if (audioRefs.current[cleanedKey]) {
            try { audioRefs.current[cleanedKey].pause(); } catch (e) { }
            delete audioRefs.current[cleanedKey];
        }

        setTracks(prev => prev.filter(t => String(t.id) !== sid));

        // remove matching item from uploadedData reducer (if present)
        try {
            if (uploadedData) {
                if (Array.isArray(uploadedData.data)) {
                    const filtered = uploadedData.data.filter(item => String(item._id) !== sid);
                    dispatch(uploadData(Object.assign({}, uploadedData, { data: filtered })));
                } else if (Array.isArray(uploadedData)) {
                    const filtered = uploadedData.filter(item => String(item._id) !== sid);
                    dispatch(uploadData(filtered));
                }
            }
        } catch (e) {
            console.debug('Failed to update uploadedData after deleteTrack', e);
        }
    }

    const handleCreatePreview = () => {
        if (demoMode) return;

        if (selectedCount < 2) {
            showToast('warning', 'Select or Upload atleast two tracks to create mix preview!')
            return;
        }

        // Map selected tracks to API format
        const selectedTracks = tracks.filter(t =>
            t.selected &&
            !String(t.id).startsWith('uploaded-') &&
            (t.fileUrl || t.originalUrl || t.cleanedAudioUrl)
        );
        const localGenre = localStorage.getItem('genre');
        const localSampleRate = localStorage.getItem('sampleRate');

        const formattedTracks = selectedTracks
            .filter(track => track.fileUrl || track.originalUrl || track.cleanedAudioUrl)
            .map(track => {
                // Map pan preference
                const panMapping = {
                    'L': 'LEFT',
                    'C': 'CENTRE',
                    'R': 'RIGHT',
                    '': 'NO_PREFERENCE'
                };

                // Map importance/presence setting
                const presenceMapping = {
                    'Normal': 'NORMAL',
                    'Lead': 'LEAD',
                    'Background': 'BACKGROUND'
                };

                // Map reverb preference
                const reverbMapping = {
                    'None': 'NONE',
                    'Low': 'LOW',
                    'Medium': 'MEDIUM',
                    'High': 'HIGH'
                };

                return {
                    trackURL: track.fileUrl || track.originalUrl || track.cleanedAudioUrl,
                    instrumentGroup: track.category,
                    presenceSetting: presenceMapping[track.importance] || 'NORMAL',
                    panPreference: panMapping[track.pan] || 'NO_PREFERENCE',
                    reverbPreference: reverbMapping[track.reverb] || 'NONE'
                };
            });

        if (formattedTracks.length < 2) {
            showToast('warning', 'Select or Upload at least two valid tracks to create mix preview!')
            return;
        }

        const requestBody = {
            id: id,
            tracks: formattedTracks,
            musicalStyle: localGenre || selectedGenre || 'POP',
            sampleRate: String(localSampleRate) || '44100',
            returnStems: false
        };
        dispatch(mixPreviewData({}));

        // Dispatch your action here
        dispatch(createMixPreview(requestBody, setShowLoader));
    }

    const handleAudioClean = (track) => {
        if (demoMode) return;

        const reqBody = {
            fileLocation: track?.fileUrl,
            soundSource: track?.category,
            fileId: track?.id
        }
        setReqData(reqBody)
        if (hasActiveSubscription) {
            if (!creditDetails?.data || creditDetails?.data?.remaining <= 2) {
                showToast("info", "You are out of credits. Please purchase more to continue.");
                setShowCreditPlanModal(true);
                return;
            }
            setCleanedTrackId(track.id)
            dispatch(getAudioClean(reqBody, setCleanAudioLoading))
        } else {
            setShowAudioCleanModal(true)
        }
    }

    const handleUploadClick = () => {
        if (demoMode) return;
        // If both mix preview and uploadedData contain data, clear them and open the MixesFileUpload dropzone
        const hasMixPreview = !!(mixPreview && mixPreview.data && Object.keys(mixPreview.data).length > 0);
        const hasUploaded = !!(uploadedData && uploadedData.data && Array.isArray(uploadedData.data) && uploadedData.data.length > 0);

        if (hasMixPreview && hasUploaded) {
            // Only clear mixPreview so we return to the Multitrack UI (keep uploadedData)
            dispatch(mixPreviewData({}));
            return;
        }

        if (fileInputRef.current) fileInputRef.current.click()
    }

    const handleUploadNew = () => {
        handleUploadClick()
        setActiveTab('multitrack')
    }

    const handleFilesSelected = (e) => {
        const files = e.target?.files ? Array.from(e.target.files) : []
        if (!files || files.length === 0) return

        const fileArray = Array.isArray(files) ? files : (files && files.length ? Array.from(files) : [files]);

        // Validate files
        const validFiles = fileArray.filter(f => f && f instanceof File);

        if (validFiles.length === 0) {
            showToast("error", "No valid files selected");
            return;
        }

        dispatch(mixPreviewData({}));
        setIsUploadingLocal(true)
        // Pass array of File objects directly (NOT FormData)
        dispatch(uploadAndGetPreview(
            toast,
            validFiles,
            setIsUploadingLocal,
            'mixing-engineer',
            true,
            '',
            () => { }
        ));
        e.target.value = ''
    }

    const setTrackCleaned = (track, cleaned = true) => {
        if (!cleaned) {
            // Discard: remove temporary cleaned preview immediately
            setTracks(prev => prev.map(t => t.id === track.id ? { ...t, cleaned: false, cleanedPreview: false, cleanedData: null, cleanedAudioUrl: null, cleanedAudioId: null } : t));
            setCleanedTrackId(null);
            return;
        }

        // For Keep: start save flow
        let payload = null;
        if (Array.isArray(cleanAudio)) {
            payload = cleanAudio.find(p => String(p.fileId) === String(track.id) || String(p.data?._id) === String(track.id) || String(p._id) === String(track.id)) || null;
        } else if (cleanAudio && typeof cleanAudio === 'object') {
            payload = cleanAudio;
        }

        const extractedId = payload?._id || payload?.data?._id || payload?.id || null;
        const roex_response = payload?.roex_response || payload?.data?.roex_response || track?.category || null;
        const cleaned_url = payload?.roex_response?.audioCleanupResults?.cleaned_audio_file_location || payload?.data?.roex_response?.audioCleanupResults?.cleaned_audio_file_location || null;

        const reqBody = {
            fileId: extractedId || track?.id,
            roex_response: roex_response,
            cleaned_url: cleaned_url
        };

        // Set cleaned: true to show check icon, clear cleanedTrackId to hide section
        setSavingCleanedTrackId(track.id);
        setTracks(prev => prev.map(t => t.id === track.id ? { ...t, cleaned: true, cleanedPreview: false, cleanedData: null, cleanedAudioUrl: cleaned_url, fileUrl: cleaned_url, cleanedAudioId: extractedId } : t));
        setCleanedTrackId(null); // This hides the Audio Clean section

        dispatch(saveCleanedAudio(reqBody, setCleanAudioLoading));
    }

    // Hide the Audio Clean section after the saveCleanedAudio reducer reports completion for the saving track
    useEffect(() => {
        if (!savingCleanedTrackId) return;
        if (!saveCleanedAudio || (Object.keys(saveCleanedAudio).length === 0)) return;

        const payload = saveCleanedAudio;

        const matches = () => {
            try {
                if (String(payload.fileId) === String(savingCleanedTrackId)) return true;
                if (String(payload._id) === String(savingCleanedTrackId)) return true;
                if (String(payload.data?._id) === String(savingCleanedTrackId)) return true;
                if (Array.isArray(payload) && payload.some(p => String(p.fileId) === String(savingCleanedTrackId) || String(p._id) === String(savingCleanedTrackId) || String(p.data?._id) === String(savingCleanedTrackId))) return true;
            } catch (e) {
                return false;
            }
            return false;
        }

        if (matches()) {
            setTracks(prev => prev.map(t => t.id === savingCleanedTrackId ? { ...t, cleaned: true } : t));
            setSavingCleanedTrackId(null);
            setCleanedTrackId(null);
            showToast('success', 'Cleaned audio saved');
        }
    }, [saveCleanedAudio, savingCleanedTrackId]);

    const handleCoverImageClick = () => {
        if (coverImageInputRef.current) coverImageInputRef.current.click();
    }

    const handleCoverImageSelected = (e) => {
        const file = e.target?.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            showToast('error', 'Please select a valid image file');
            return;
        }

        // Create preview URL
        const imageUrl = URL.createObjectURL(file);
        setCoverImage(imageUrl);

        // Optional: Upload to server
        const formData = new FormData();
        formData.append('id', id)
        formData.append('file', file);
        dispatch(uploadProjectImage(formData));

        e.target.value = '';
    }

    const handleRenameProject = (newName) => {
        dispatch(renameMixTrack(id, { newName: newName }, false));
    }

    const onExit = () => {
        dispatch(clearUploadData());
        dispatch(uploadData({}))
        dispatch(mixPreviewData({}))
        navigate('/admin/urstudio/mixing-engineer')
    }

    return (
        <>
            <div className='multitrack-page'>
                <input ref={fileInputRef} type="file" accept="audio/*,.wav,.flac,.aiff,.aif" className="hidden" onChange={handleFilesSelected} />
                <div className="relative">
                    <div className="bg-theme_clr1 text-white rounded-xl p-4">
                        <div className="bg-white mb-4 px-3 py-2">
                            <button onClick={onExit} className="bg-theme_clr2 bg-opacity-50 text-theme_clr4 font-medium text-sm rounded-md px-4 py-2"><i className="bi bi-arrow-left-square mr-1"></i> Exit</button>
                        </div>

                        <div className="flex flex-col md:flex-row gap-4">
                            <aside className="left-section w-[236px] max-h-[380px] bg-gradient-to-b from-slate-300 via-theme_clr1 via-55% to-theme_clr2 border border-white rounded-xl p-2">
                                {/* <div className="img-wrapper mb-3">
                                <img src={WireframeArt} alt="Wireframe Art" className="w-full max-w-[220px]" />
                            </div> */}
                                <div data-tour="upload-music-art" className="img-wrapper mb-3 relative group cursor-pointer" onClick={handleCoverImageClick}>
                                    <img src={coverImage} alt="Wireframe Art" className="w-full max-w-[220px] h-[220px] object-cover rounded-lg" />

                                    {/* Overlay with upload icon - shows on hover */}
                                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center rounded-lg max-w-[220px]">
                                        <i className="bi bi-cloud-upload text-white text-4xl"></i>
                                    </div>

                                    {/* Hidden file input */}
                                    <input
                                        ref={coverImageInputRef}
                                        type="file"
                                        accept="image/*,.jpg,.jpeg,.png,.gif,.webp"
                                        className="hidden"
                                        onChange={handleCoverImageSelected}
                                    />
                                </div>
                                <div data-tour="genre-and-edit-project" className="wrapper">
                                    <GenreDropdown
                                        value={selectedGenre}
                                        onChange={setSelectedGenre}
                                        placeholder="Choose a genre..."
                                    />
                                    <div className='mt-4 text-lg'>
                                        <EditableTrackName
                                            demo={demoMode}
                                            name={demoMode ? "Project Name" : projectName}
                                            onSave={(newName) => handleRenameProject(newName)}
                                        />
                                    </div>
                                </div>
                            </aside>
                            <div className="right-section md:max-w-[calc(100%-252px)] w-full">
                                {/* Tabs */}
                                <div data-tour="tabs" className="bg-white inline-flex rounded-full p-1 mb-4">
                                    {tabs.map(({ id, label, icon: Icon }) => {
                                        const active = activeTab === id
                                        return (
                                            <button key={id} onClick={() => setActiveTab(id)}
                                                className={`flex items-center gap-1 px-4 py-2 rounded-full text-theme_clr4 transition-all ${active ? "bg-theme_clr1" : ""}`}>
                                                <img src={Icon} alt="Icon" />
                                                <span className="text-sm font-medium">{label}</span>
                                            </button>
                                        )
                                    })}
                                </div>
                                {!mixPreview?.data &&
                                    <>
                                        <hr className="mb-4 border-gray-900" />
                                        <div className="bg-theme_clr2 flex items-center gap-2 rounded-xl p-4 mb-4">
                                            Select tracks to create a mix preview
                                            <img src={ButtonIcon} alt="Button Icon" />
                                        </div>

                                        <hr className="mb-4 border-gray-900" />
                                    </>
                                }
                                {activeTab === "multitrack" && (
                                    showLoader ?
                                        <ProcessingLoader isDemo={demoMode} /> :
                                        <>
                                            {/* Toolbar */}
                                            <div className="flex flex-wrap items-center gap-4 text-sm mb-4">
                                                {/* left column - takes available space so right controls stay on the right */}
                                                <div className="flex-1">
                                                    {!mixPreview?.data &&
                                                        <div className="flex flex-wrap items-center gap-3">
                                                            <button onClick={toggleSelectAll}>Select All</button>
                                                            <button onClick={deleteSelected} className="flex items-center gap-2 bg-theme_clr2 px-3 py-2 rounded-full">
                                                                <i className="bi bi-trash3-fill"></i> Delete
                                                            </button>
                                                            <span className="opacity-80">{selectedCount} selected</span>
                                                        </div>
                                                    }
                                                </div>

                                                {/* right column - always aligned to the right */}
                                                <div className="flex flex-wrap items-center gap-3">
                                                    <button
                                                        data-tour="upload-more"
                                                        onClick={handleUploadClick}
                                                        disabled={isUploadingLocal}
                                                        className="font-medium flex items-center gap-2 bg-theme_clr4 px-4 py-2 rounded-full disabled:opacity-50"
                                                    >
                                                        <i className="bi bi-upload"></i> {isUploadingLocal ? 'Uploading...' : 'Upload Tracks'}
                                                    </button>
                                                    {!mixPreview?.data &&
                                                        <button data-tour="create-preview" onClick={handleCreatePreview} className="bg-white font-medium text-theme_clr1 px-4 py-2 rounded-full">
                                                            Create Preview
                                                        </button>
                                                    }
                                                </div>
                                            </div>

                                            {mixPreview?.data ?
                                                <div className='mt-4'>
                                                    <PreviewSection />
                                                </div>
                                                : <div data-tour="tracks-list" className="table-wrapper w-full overflow-x-auto">
                                                    <div className="flex flex-col gap-5 min-w-[800px]">
                                                        {/* GROUPS */}
                                                        {Object.entries(groupedTracks).map(([category, items]) => {
                                                            const allSelected = items.every(t => t.selected)
                                                            const categoryLabel = defaultCategories.find(c => c.value === category)?.label || category

                                                            return (
                                                                <div key={category} className="item-row">
                                                                    {/* GROUP HEADER */}
                                                                    <div className="flex items-center gap-3 mb-3 ps-4">
                                                                        <input type="checkbox" className="w-4 h-4 border-2 border-white bg-transparent" checked={allSelected} onChange={() => toggleGroup(category)} />
                                                                        <span className="font-medium">
                                                                            {categoryLabel} <span className="opacity-60 ml-2">{items.length}</span>
                                                                        </span>
                                                                    </div>

                                                                    {/* Tracks */}
                                                                    <div className="item-wrapper space-y-3">
                                                                        {items.map(track => {
                                                                            const extension = track?.name.split(".").pop()
                                                                            const isValidExtForCleanup = ['wav', 'flac'].includes(extension);
                                                                            const status = track.roexStatus || '';
                                                                            const isReady = status === 'UPLOADED_TO_ROEX';
                                                                            let progress = 0;
                                                                            let label = '';
                                                                            let isIndeterminate = false;
                                                                            if (status === 'LOCAL_UPLOADING') {
                                                                                progress = 15;
                                                                                label = '15%';
                                                                                isIndeterminate = true;
                                                                            } else if (status === "QUEUED_FOR_ROEX") {
                                                                                progress = 60;
                                                                                label = '60%';
                                                                            } else if (status === 'UPLOADING_TO_ROEX') {
                                                                                progress = 75;
                                                                                label = '75%';
                                                                            } else if (status === 'UPLOADED_TO_ROEX') {
                                                                                progress = 100;
                                                                                label = '100%';
                                                                            } else if (status === 'FAILED') {
                                                                                progress = 100;
                                                                                label = 'Failed';
                                                                            }

                                                                            return (
                                                                                <div key={track.id} className="rounded-xl bg-theme_clr2">
                                                                                    {!isReady && (
                                                                                        <div className="p-4">
                                                                                            <div className="flex justify-between text-sm mb-1">
                                                                                                <span className="truncate max-w-[70%]">
                                                                                                    {track.name}
                                                                                                </span>
                                                                                                <span>{label}</span>
                                                                                            </div>
                                                                                            <div className="w-full bg-gray-200 rounded-full h-2">
                                                                                                <div
                                                                                                    className={`h-2 rounded-full transition-all duration-1000 ease-in-out ${status === "FAILED"
                                                                                                        ? "bg-red-500"
                                                                                                        : isIndeterminate
                                                                                                            ? "bg-theme_clr1 animate-pulse"
                                                                                                            : "bg-theme_clr1"
                                                                                                        }`}
                                                                                                    style={{ width: `${progress}%` }}
                                                                                                />
                                                                                            </div>
                                                                                        </div>
                                                                                    )}
                                                                                    {isReady && (
                                                                                        <div className={`flex items-center justify-between gap-4 rounded-xl px-4 py-2 ${track.selected ? "bg-theme_clr2" : "bg-theme_clr2/75"}`}>
                                                                                            <div className="flex items-center gap-3">
                                                                                                <input type="checkbox" className="w-4 h-4 border-2 border-white bg-transparent" checked={track.selected} onChange={() => toggleTrack(track.id)} />
                                                                                                <EditableTrackName
                                                                                                    name={track.name}
                                                                                                    onSave={(newName) => setTrackName(track.id, newName)}
                                                                                                />
                                                                                            </div>

                                                                                            <div className="flex items-center gap-4 flex-nowrap">
                                                                                                <div className="flex flex-1 items-center gap-2">
                                                                                                    <CategoryComboBox
                                                                                                        value={track.category}
                                                                                                        onChange={(val) => setTrackCategory(track.id, val)}
                                                                                                    />
                                                                                                    <Tooltip
                                                                                                        text={"Presence Setting"}
                                                                                                        style={{ bg: "bg-theme_clr2", text: "text-white" }}
                                                                                                    >
                                                                                                        <IconDropdown
                                                                                                            value={track.importance}
                                                                                                            options={importanceLevels}
                                                                                                            onChange={(val) => setTrackImportance(track.id, val)}
                                                                                                            label="Presence Setting"
                                                                                                            icon={PieGroupIcon}
                                                                                                            width={"20px"}
                                                                                                        />
                                                                                                    </Tooltip>
                                                                                                    <Tooltip
                                                                                                        text={"Reverb"}
                                                                                                        style={{ bg: "bg-theme_clr2", text: "text-white" }}
                                                                                                    >
                                                                                                        <IconDropdown
                                                                                                            value={track.reverb}
                                                                                                            options={reverbLevels}
                                                                                                            onChange={(val) => setTrackReverb(track.id, val)}
                                                                                                            label="Reverb"
                                                                                                            icon={GoalIcon}
                                                                                                            width={"30px"}
                                                                                                        />
                                                                                                    </Tooltip>
                                                                                                </div>
                                                                                                <div className="flex items-center gap-3">
                                                                                                    <Tooltip
                                                                                                        text={"Pan Preference"}
                                                                                                        style={{ bg: "bg-theme_clr2", text: "text-white" }}
                                                                                                    >
                                                                                                        <div className="bg-black/25 flex items-center gap-1 rounded-full text-xs cursor-pointer p-1">
                                                                                                            {['L', 'C', 'R'].map((p) => {
                                                                                                                const active = track.pan === p;
                                                                                                                return (
                                                                                                                    <div
                                                                                                                        key={p}
                                                                                                                        type="button"
                                                                                                                        onClick={() => setTrackPan(track.id, p)}
                                                                                                                        className={`h-6 w-6 flex items-center justify-center rounded-full transition-colors text-sm font-semibold ${active ? 'bg-black/20 text-white' : 'bg-transparent text-white/90'}`}
                                                                                                                        aria-pressed={active}
                                                                                                                        aria-label={`Pan ${p}`}
                                                                                                                    >
                                                                                                                        {p}
                                                                                                                    </div>
                                                                                                                )
                                                                                                            })}
                                                                                                        </div>
                                                                                                    </Tooltip>
                                                                                                    {(track.cleanerEnabled && isValidExtForCleanup) &&
                                                                                                        (track.cleaned ?
                                                                                                            <Tooltip
                                                                                                                text={"Audio Cleaned"}
                                                                                                                style={{ bg: "bg-theme_clr2", text: "text-white" }}
                                                                                                            >
                                                                                                                <div className="min-w-8 w-8 h-8 bg-theme_clr1 text-center border-[2px] border-white rounded-full">
                                                                                                                    <i className="bi bi-check-circle-fill text-white text-lg"></i>
                                                                                                                </div>
                                                                                                            </Tooltip> :
                                                                                                            <Tooltip
                                                                                                                text={"For Pro users, this will deduct 2 AI credits from your balance."}
                                                                                                                style={{ bg: "bg-theme_clr2", text: "text-white" }}
                                                                                                            >
                                                                                                                <div onClick={() => handleAudioClean(track)} className="min-w-8 w-8 h-8 bg-theme_clr1 text-center border-[2px] border-white rounded-full cursor-pointer">
                                                                                                                    <img src={BrushIcon} alt="Brush Icon" className="h-6 w-6" />
                                                                                                                </div>
                                                                                                            </Tooltip>
                                                                                                        )
                                                                                                    }
                                                                                                    <Tooltip
                                                                                                        text={track.playing ? "Pause Audio" : "Play Audio"}
                                                                                                        style={{ bg: "bg-theme_clr2", text: "text-white" }}
                                                                                                    >
                                                                                                        <div onClick={() => togglePlay(track.id)} className="bg-gradient-to-r from-[#1B2028] to-[#FFFFFF] border-2 border-white drop-shadow-xl text-black rounded-full w-7 h-7 flex items-center justify-center">
                                                                                                            <i className={`text-lg bi ${track.playing ? "bi-pause-fill" : "bi-play-fill"}`} />
                                                                                                        </div>
                                                                                                    </Tooltip>
                                                                                                </div>
                                                                                                <div className="flex items-center gap-4">
                                                                                                    <img src={WaveIcon} alt="Wave Icon" className="w-32" />
                                                                                                    {/* <span className="text-sm text-white/60">{track.duration}</span> */}
                                                                                                    <span className="text-sm text-white/60 min-w-[45px] text-center">
                                                                                                        {(!track.duration || track.duration === '0:00' || track.duration.includes('NaN'))
                                                                                                            ? <span className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                                                                                                            : track.duration
                                                                                                        }
                                                                                                    </span>
                                                                                                    <OptionsMenu
                                                                                                        trackId={track.id}
                                                                                                        onDelete={deleteTrack}
                                                                                                    />
                                                                                                </div>
                                                                                            </div>
                                                                                        </div>
                                                                                    )}
                                                                                    {/*Clean Audio Section */}
                                                                                    {(cleanedTrackId === track.id || (demoMode && track.cleanedPreview)) &&
                                                                                        <>
                                                                                            <hr className="mb-1 border-gray-300" />
                                                                                            <div className="wrapper w-full flex items-center gap-4 px-4 py-2">
                                                                                                <div className="flex items-center gap-2 max-w-[30%]">
                                                                                                    {cleanAudioLoading ?
                                                                                                        <div className="flex justify-center items-center">
                                                                                                            <div className="animate-spin rounded-full h-10 w-10 border-b-4 border-blue-500 border-t-transparent"></div>
                                                                                                        </div> :
                                                                                                        <span className="bg-theme_clr1/15 border-2 border-theme_clr1 h-10 w-10 min-w-10 flex items-center justify-center rounded-full">
                                                                                                            <i className="bi bi-check-circle-fill text-white text-lg"></i>
                                                                                                        </span>
                                                                                                    }
                                                                                                    <div className="info">
                                                                                                        <div className="text-sm text-white mb-1">{(cleanAudioLoading && !demoMode) ? 'Your Audio is cleaning' : 'Audio Cleaned'}</div>
                                                                                                        <div className="text-[13px] text-white/60">{(cleanAudioLoading && !demoMode) ? 'Please wait' : 'Preview the result and choose to keep or discard the changes'}</div>
                                                                                                    </div>
                                                                                                </div>
                                                                                                <div className="flex flex-1 items-center gap-4">
                                                                                                    <button disabled={cleanAudioLoading} onClick={() => togglePlayCleaned(track.id)} className="bg-black/30 border-2 border-gray-600 drop-shadow-xl text-white rounded-full min-w-9 w-9 h-9 flex items-center justify-center">
                                                                                                        <i className={`text-lg bi ${track.playingCleaned ? "bi-pause-fill" : "bi-play-fill"}`} />
                                                                                                    </button>
                                                                                                    <img src={WaveIcon} alt="Wave Icon" className="w-full" />
                                                                                                    <span className="text-sm text-white/60">{track.cleanedDuration || track.duration || '0:00'}</span>
                                                                                                </div>
                                                                                                <div className="flex justify-end gap-2">
                                                                                                    <button disabled={demoMode} onClick={() => setTrackCleaned(track, true)} className="bg-theme_clr1 text-white rounded-lg text-sm text-nowrap px-3 py-2">
                                                                                                        <i className="bi bi-check2 mr-1"></i> Keep
                                                                                                    </button>
                                                                                                    <button disabled={demoMode} onClick={() => setTrackCleaned(track, false)} className="bg-theme_clr4 text-white rounded-lg text-sm text-nowrap px-3 py-2">
                                                                                                        <i className="bi bi-x-lg mr-1"></i> Discard
                                                                                                    </button>
                                                                                                </div>
                                                                                            </div>
                                                                                        </>
                                                                                    }
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            )
                                                        })}
                                                    </div>
                                                </div>
                                            }

                                        </>
                                )}
                                {activeTab === "mixes" && (
                                    <div>
                                        <MixesLibrary addNew={handleUploadNew} showHeader={false} setActiveTab={setActiveTab} />
                                    </div>
                                )}

                                {activeTab === "masters" && (
                                    <div>
                                        <MastersLibrary addNew={handleUploadNew} />
                                    </div>
                                )}

                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <PricingPlanModal
                isOpen={showPricingPlanModal}
                onClose={() => setShowPricingPlanModal(false)}
                returnUrl={window.location.origin + "/admin/urstudio/mixing-engineer?tab=multitrack"}
            />
            <AudioCleanPackModal
                isOpen={showAudioCleanModal}
                onClose={() => setShowAudioCleanModal(false)}
                requestData={reqData}
                setAudioLoading={setCleanAudioLoading}
                onPaymentSuccess={() => {
                    setShowAudioCleanModal(false);
                    setCleanedTrackId(reqData?.fileId);
                }}
            />
            <CreditPlanModal
                isOpen={showCreditPlanModal}
                onClose={() => setShowCreditPlanModal(false)}
            />
        </>
    )
}

export default Multitrack